import { FC, useEffect, useEffectEvent, useState } from 'react';
import { StanModal } from '@/components';
import { previewBroadcast } from '@/lib/api/email-flows/api';
import { formatRecipientCount } from '@/features/email-flows/logic/flowFormatting.logic';
import type { Tag } from '@/features/email-flows/types/audience.types';
import { formatNumber } from '@/lib/helpers';

interface PreviewBroadcastModalProps {
    isOpen: boolean;
    flowId?: number;
    flowEmailId?: number;
    tags: Tag[];
    recipientCount?: number;
    disabled?: boolean;
    isLoading?: boolean;
    onClose: () => void;
    onSendNow: () => Promise<void>;
    onSchedule: () => void;
}

export const PreviewBroadcastModal: FC<PreviewBroadcastModalProps> = ({
    isOpen,
    flowId,
    flowEmailId,
    tags,
    recipientCount: initialCount,
    disabled = false,
    isLoading = false,
    onClose,
    onSendNow,
    onSchedule,
}) => {
    const [recipientCount, setRecipientCount] = useState(initialCount || 0);

    const fetchRecipientCount = useEffectEvent(async () => {
        try {
            const data = await previewBroadcast({
                flow_id: flowId,
                flow_email_id: flowEmailId,
                tags,
            });
            setRecipientCount(data.count);
        } catch (error) {
            console.error('Error fetching recipient count:', error);
            setRecipientCount(0);
        }
    });

    const handleRecipientCountUpdate = useEffectEvent((count: number | undefined) => {
        if (count) {
            setRecipientCount(count);
        } else {
            fetchRecipientCount();
        }
    });

    useEffect(() => {
        if (isOpen) {
            handleRecipientCountUpdate(initialCount);
        }
    }, [isOpen, initialCount]);

    return (
        <StanModal
            id='preview-broadcast-modal'
            isOpen={isOpen}
            onClose={onClose}
            title='Confirming!'
            size='sm'
            primaryActionLabel='Send Now'
            onPrimaryAction={onSendNow}
            primaryActionDisabled={!recipientCount || disabled || isLoading}
            primaryActionLoading={isLoading}
            secondaryActionLabel='Schedule for Later'
            onSecondaryAction={onSchedule}
            secondaryActionDisabled={disabled || isLoading}
        >
            <div className='flex flex-col items-center justify-center py-6'>
                {recipientCount > 0 ? (
                    <>
                        <p className='text-gray text-caption-1-regular -mt-8 text-center'>
                            You are about to send this email to
                        </p>
                        <div className='bg-bg-subtle mt-8 flex h-56 w-full flex-col items-center justify-center gap-2 rounded-2xl md:gap-4'>
                            <p className='text-dark text-8xl leading-none font-bold tracking-tight'>
                                {formatNumber(recipientCount, { decimals: 0 })}
                            </p>
                            <p className='text-gray text-caption-1-bold'>{formatRecipientCount(recipientCount)}</p>
                        </div>
                    </>
                ) : (
                    <div className='border-warning-intense bg-warning-dimmed -mt-2 rounded-md border px-4 py-3'>
                        <p className='text-warning-bold text-caption-1-regular text-center'>
                            Please select at least one audience tag before sending this broadcast.
                        </p>
                    </div>
                )}
            </div>
        </StanModal>
    );
};
